import React, { useEffect, useState, useRef } from "react";
import { cqAPI, getBid } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Button,
  useTheme,
  useColorMode,
  IconButton,
  Image,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Link,
} from "@chakra-ui/react";
import EditableText from "../EditableText/EditableText";
import { useNavigate } from 'react-router-dom';
import "./AllCustomers.css";
import { AddIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon } from "@chakra-ui/icons";

import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

function AllCustomers() {
  const googleMap = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries
  });

  const [customers, setCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [newBusiness, setNewBusiness] = useState({});
  const navigate = useNavigate()

  const autocompleteRef = useRef({});
  const {
    isOpen: isNewBusinessOpen,
    onOpen: onNewBusinessOpen,
    onClose,
  } = useDisclosure();
  const onNewBusinessClose = () => {
    setNewBusiness({}); // Reset newUser state
    onClose(); // Use the onClose function from useDisclosure
  };
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const fetchAllCustomers = async () => {
    const bid = getBid();
    const response = bid
      ? await cqAPI.get(`/locations?parents_only=1`)
      : await cqAPI.get(`/locations?parents_only=1&bid=`);
    if (response?.type === "ok"){
      setCustomers(response.data);
    }
  };

  useEffect(() => {
    fetchAllCustomers();
  }, []);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const updateBusinessProfile = async (customer_id, field, value) => {
    const response = await cqAPI.put(`/settings/business?bid=${customer_id}`, {
      field: "groupname",
      value,
    });
    if (response.type == "ok") {
      setCustomers(
        customers.map((customer) => {
          if (customer.bid === customer_id) {
            customer.groupname = value;
          }
          return customer;
        })
      );
      return true;
    }
    return false;
  };

  function NewBusinessModal({ isOpen, isMapsLoaded, onClose, autocompleteRef, customers }) {
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState("");
    const toast = useToast();
    const options = {
      types: ["establishment"], // Specify that you only want to search for establishments
    };

    const handleGroupSelect = (event) => {
      setSelectedGroup(event.target.value);
    };

    const handlePlaceSelect = () => {
      const place = autocompleteRef.current.getPlace();
      const isDentalOffice = place.types && place.types.includes("dentist");

      if (!isDentalOffice) {
        toast({
          title: "Invalid selection.",
          description: "You can only select a dental office.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }

      setSelectedPlace({
        name: place.name,
        address: place.formatted_address,
        location: place.geometry.location,
        place: place,
      });
    };

    const handleBack = () => {
      setSelectedPlace(null); // Reset selected place to allow a new selection
    };

    const confirmSelection = async () => {
      onClose(); // Close the modal
      const response = await cqAPI.post(`/settings/business`, {
        place: selectedPlace.place,
        ...(selectedGroup ? { parent: selectedGroup } : {}),
      });
      if (response.type == "ok") {
        await fetchAllCustomers();
      } else if (response.msg) {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select a Dental Office</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isMapsLoaded && selectedPlace ? (
              <>
                <Text fontWeight="bold">{selectedPlace.name}</Text>
                <Text mb={4}>{selectedPlace.address}</Text>
                <Box boxSize="sm">
                  <Image
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${selectedPlace.location.lat()},${selectedPlace.location.lng()}&zoom=15&size=400x300&markers=|${selectedPlace.location.lat()},${selectedPlace.location.lng()}&key=${
                      process.env.REACT_APP_MAP_API
                    }`}
                    alt="Selected Place"
                  />
                </Box>
                <Button colorScheme="blue" mt={4} onClick={confirmSelection}>
                  Confirm Selection
                </Button>
                <Button colorScheme="gray" mt={4} ml={3} onClick={handleBack}>
                  Back
                </Button>
              </>
            ) : (
              <>
                <FormControl id="group-select" mt={4}>
                  <FormLabel>Select a Group</FormLabel>
                  <Select onChange={handleGroupSelect}>
                    <option value="">New Group</option>
                    {customers.map((customer) => (
                      <option key={customer.bid} value={customer.bid}>
                        {customer.groupname || customer.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl id="google-maps-autocomplete" paddingTop="15px">
                  <FormLabel>Dental Office</FormLabel>
                  {isMapsLoaded && (
                      <Autocomplete
                        options={options}
                        onPlaceChanged={handlePlaceSelect}
                        onLoad={(autocomplete) => {
                          autocompleteRef.current = autocomplete;
                        }}
                      >
                        <Input type="text" placeholder="Type to search..." />
                      </Autocomplete>
                  )}
                </FormControl>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            {!selectedPlace && (
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <NewBusinessModal
        isOpen={isNewBusinessOpen}
        isMapsLoaded={googleMap.isLoaded}
        onClose={onNewBusinessClose}
        autocompleteRef={autocompleteRef}
        customers={customers}
      />

      <Flex justifyContent="flex-end" mb={4}>
        <Button leftIcon={<AddIcon />} onClick={onNewBusinessOpen}>
          Add Location
        </Button>
      </Flex>
      <VStack spacing={4} align="stretch">
        {customers.map((customer) => (
          <Box key={customer.bid}>
            <Flex
              p={2}
              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
              justifyContent="space-between"
            >
              <HStack spacing={8} flex="1">
                <Box minWidth="40px">
                  {" "}
                  {/* Reserve space for the button */}
                  {(customer.locations || []).length > 0 && (
                    <IconButton
                      icon={
                        expandedRows[customer.bid] ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      onClick={() => toggleExpand(customer.bid)}
                      aria-label="Expand"
                    />
                  )}
                </Box>
                <Flex
                  direction="column"
                  justifyContent="left"
                  flexBasis="70%"
                >
                  <Flex>
                    <EditableText
                      initialValue={customer.groupname || customer.name}
                      placeholder="Group Name"
                      pattern={/[a-zA-Z]+/}
                      invalid="Cannot be empty"
                      onSave={(newValue) =>
                        updateBusinessProfile(
                          customer.bid,
                          "groupname",
                          newValue
                        )
                      }
                    />
                  </Flex>
                </Flex>
                <Text flexBasis="20%" onClick={()=>navigate(`/?bid=${customer.bid}`)}>
                  {customer.locations && customer.locations.length}
                </Text>
                <Flex flexBasis="10%" onClick={()=>navigate(`/?bid=${customer.bid}`)}>
                  <ChevronRightIcon />
                </Flex>

              </HStack>
            </Flex>
            {expandedRows[customer.bid] && (
              <VStack align="stretch" pl={12} mt={2}>
                {" "}
                {/* Adjust paddingLeft to match the button's presence */}
                {(customer.locations || []).map((location, index) => (
                  <Link textAlign="left" key={index} fontSize="sm" onClick={()=>navigate(`/?bid=${location._id}`)}>
                    {location.name}
                  </Link>
                ))}
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

export default AllCustomers;
